import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import Bookmark from '@components/Bookmark';
import { BookmarkSize } from '@components/Bookmark/constants';
import Button from '@components/Button';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import ClientRenderedContent from '@components/ClientRenderedContent';
import SvgGiftboxIcon from '@components/Images/Gift/GiftboxIcon';
import IconShareArrow from '@components/Images/Rebranding/IconShareArrow';
import ListenButton from '@components/ListenButton';
import { ONBOARDING_STEP_IDS } from '@components/UserOnboardingTooltip/constants';
import { registerOnboardingAnchor } from '@components/UserOnboardingTooltip/utils';
import TooltipWrapper from '@components/pages/AI/DeepResearch/Chatbot/Controls/TooltipWrapper';
import { useAppContext } from '@contexts/AppContext';
import { useArticleCommentsSummary } from '@hooks/useArticleCommentsSummary';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import useNativeAppVersionCheck from '@hooks/useNativeAppVersionCheck';
import useServerSideRendering from '@hooks/useServerSideRendering';
import type { Author } from '@interfaces/authors';
import { ClickTarget, ContentType, click } from '@tracking';

import ArticleCommentJumpLink from './ArticleCommentJumpLink';
import { useAudio } from './AudioContext';
import ShareArticleModal from './ShareArticleModal';

interface DetailsProps {
  articleId: number;
  authors: Author[];
  publishedAt: string;
}

const STICKY_NAV_PLUS_SPACE = 100;

const SHARE_ARTICLE_LABEL = 'Gift article';
const COPY_LINK_LABEL = 'Copy article link';

const ARTICLE_ACTION_ICON_BUTTON_CLASS =
  'share-button m-0 flex shrink-0 cursor-pointer touch-manipulation items-center justify-center border border-solid border-grayscale-gray bg-white p-0 text-grayscale-gray outline-none ring-0 transition-[color,background-color,border-color] duration-150 ease-in-out [-webkit-tap-highlight-color:transparent] [@media(hover:hover)_and_(pointer:fine)]:hover:border-primary-magenta [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary-magenta [@media(hover:hover)_and_(pointer:fine)]:hover:text-white dark:bg-[var(--light-grayscale-charcoal)] dark:text-white [@media(hover:hover)_and_(pointer:fine)]:dark:hover:border-white print:hidden';

const ArticleDetails = ({ articleId, authors, publishedAt }: DetailsProps) => {
  const { commentsCount, commenters } = useArticleCommentsSummary(articleId);
  const { currentUser } = useCurrentUser();
  const canShareArticle = !!currentUser?.can_share_article;
  const { audioActive, setAudioActive } = useAudio();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareButtonRef = useRef<HTMLDivElement | null>(null);
  const { initialCurrentUser, showArticleGiftButton } = useAppContext();
  const { isApp3_0OrGreater, isNativeApp } = useNativeAppVersionCheck();
  const hydrated = useServerSideRendering();
  const showListenButton =
    initialCurrentUser?.is_subscriber && (!isNativeApp || (isNativeApp && isApp3_0OrGreater));
  const { trackEvent } = useEventTracking();

  useEffect(() => {
    const el = shareButtonRef.current;
    if (canShareArticle && !isNativeApp && el) {
      registerOnboardingAnchor(ONBOARDING_STEP_IDS.ArticleShare, el);
      return () => registerOnboardingAnchor(ONBOARDING_STEP_IDS.ArticleShare, null);
    }
  }, [canShareArticle, isNativeApp]);

  const handleShareClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ShareButton }));
    setIsShareModalOpen(true);
  };

  const handleCopyLinkClick = () => {
    navigator.clipboard.writeText(window.location.href);
    trackEvent(click({ clickTarget: ClickTarget.CopyLinkButton }));
    toast.success('Link copied to clipboard');
  };

  const handleCommentJumpClick = useCallback(() => {
    const commentsSection = document.getElementById('comments-section');
    if (!commentsSection) return;
    const scrollTopDistance = commentsSection.getBoundingClientRect().top;
    window?.scrollBy({
      behavior: 'smooth',
      left: 0,
      top: scrollTopDistance - STICKY_NAV_PLUS_SPACE,
    });
  }, []);

  return (
    <>
      <div className="grid w-full grid-cols-[1fr_auto] items-start gap-x-12 [grid-template-areas:'byline_byline'_'divider_divider'_'time_actions'_'comments_comments'] md:[grid-template-areas:'byline_actions'_'divider_divider'_'time_comments']">
        {authors.length > 0 && (
          <div className="[grid-area:byline]">
            <BylineWithHeadshot
              authors={authors}
              authorClassName="author-name-body dark:text-white"
              className="max-w-sm"
            />
          </div>
        )}

        <div
          aria-hidden="true"
          className="mb-10 mt-12 border-0 border-b border-solid border-grayscale-beige [grid-area:divider]"
        />

        <ClientRenderedContent>
          <time
            className="mb-0 block font-suisse-intl-mono text-overline-sm font-normal text-grayscale-gray [grid-area:time] sm:text-overline-md lg:text-overline-sm xl:text-overline-md"
            dateTime={publishedAt}
          >
            {publishedAt}
          </time>

          {commentsCount > 0 && (
            <div className="[grid-area:comments]">
              <ArticleCommentJumpLink
                commentsCount={commentsCount}
                commenters={commenters}
                onClick={handleCommentJumpClick}
              />
            </div>
          )}
        </ClientRenderedContent>

        <div className="flex shrink-0 items-end gap-12 justify-self-end [grid-area:actions]">
          {showListenButton && (
            <ListenButton
              contentType={ContentType.Article}
              isAudioActive={audioActive}
              setIsAudioActive={setAudioActive}
            />
          )}
          {canShareArticle && !isNativeApp && (
            <>
              {showArticleGiftButton ? (
                <>
                  <TooltipWrapper
                    tooltipMessage={hydrated ? SHARE_ARTICLE_LABEL : ''}
                    tooltipPlacement="top"
                  >
                    <div
                      ref={shareButtonRef}
                      className="ml-auto"
                      data-onboarding-anchor="article-share"
                    >
                      <Button
                        as="button"
                        aria-label={SHARE_ARTICLE_LABEL}
                        className={`${ARTICLE_ACTION_ICON_BUTTON_CLASS} h-40 w-48 rounded-full`}
                        onClick={handleShareClick}
                      >
                        <SvgGiftboxIcon className="h-20 w-20" />
                      </Button>
                    </div>
                  </TooltipWrapper>
                  <TooltipWrapper
                    tooltipMessage={hydrated ? COPY_LINK_LABEL : ''}
                    tooltipPlacement="top"
                  >
                    <div>
                      <Button
                        as="button"
                        aria-label={COPY_LINK_LABEL}
                        className={`${ARTICLE_ACTION_ICON_BUTTON_CLASS} h-40 rounded-full px-[14px]`}
                        onClick={handleCopyLinkClick}
                      >
                        <IconShareArrow className="h-[17px] w-[22px]" />
                      </Button>
                    </div>
                  </TooltipWrapper>
                </>
              ) : (
                <div
                  ref={shareButtonRef}
                  className="ml-auto"
                  data-onboarding-anchor="article-share"
                >
                  <Button
                    as="button"
                    className="share-button flex items-center rounded-20 border border-solid border-grayscale-gray bg-white px-12 py-8 font-suisse-intl-mono text-button-xs text-grayscale-gray outline-none hover:transition-all sm:hover:border-primary-magenta sm:hover:bg-primary-magenta sm:hover:text-white dark:bg-[var(--light-grayscale-charcoal)] dark:text-white sm:dark:hover:border-white sm:dark:hover:text-white print:hidden"
                    onClick={handleShareClick}
                  >
                    Share
                  </Button>
                </div>
              )}
            </>
          )}
          <Bookmark
            contentId={articleId}
            contentType={ContentType.Article}
            size={showArticleGiftButton ? BookmarkSize.ArticleActions : undefined}
          />
        </div>
      </div>
      {canShareArticle && !isNativeApp && (
        <ShareArticleModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          articleId={articleId}
        />
      )}
    </>
  );
};

export default ArticleDetails;
